import { TFunction } from 'i18next';
import { CampaignCard } from './CampaignCard';
import { styled } from '@/stitches.config';
import { VolumeDistributionItem } from './VolumeDistributionItem';
import { StyledButton } from '@/common/components/base-app/atoms/Button/Button.styles';
import { Variants } from '@/common/types';
import { IVolumeDistributionAsString } from '../AddCampaignForm.types';
import { useEffect, useRef, useState } from 'react';

const ListContainer = styled('div', {
  display: 'flex',
  gap: '$4',
  marginBottom: '$5',
});

const ListItemContainer = styled('ol', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$5',
  margin: 0,
  padding: 0,
});

export const SubLeftHeading = styled('label', {
  fontSize: '$sm',
  flexBasis: '30%',
});

export const SubCenterHeading = styled('label', {
  fontSize: '$sm',
  flexBasis: '65%',
});

export const SubRightHeading = styled('label', {
  fontSize: '$sm',
  flexBasis: '5%',
});

const ButtonContainer = styled('div', {
  marginTop: '$6',
});

interface VolumeDistributionProps {
  t: TFunction;
  volumeDistribution?: IVolumeDistributionAsString[];
  onAddVolumeDistribution?: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  onRemoveVolumeDistribution?: (index: number, event: React.SyntheticEvent<HTMLButtonElement>) => void;
  onChangeVolumeDistributionItem?: (
    index: number,
    event: React.SyntheticEvent<HTMLInputElement> | string,
    key: keyof IVolumeDistributionAsString,
  ) => void;
  errors?: IVolumeDistributionAsString[];
}

export const VolumeDistribution = ({
  t,
  volumeDistribution,
  onAddVolumeDistribution,
  onRemoveVolumeDistribution,
  onChangeVolumeDistributionItem,
  errors,
}: VolumeDistributionProps): JSX.Element => {
  const [volumeDistributionNextFocus, setVolumeDistributionNextFocus] = useState<number>(
    volumeDistribution?.length ?? 0,
  );
  const lastItemRef = useRef<HTMLInputElement | null>(null);
  const [volumeDistributionLength, setVolumeDistributionLength] = useState<number>(0);

  useEffect(() => {
    if (volumeDistribution && volumeDistribution.length !== volumeDistributionLength) {
      setVolumeDistributionLength(volumeDistribution.length);
      if (lastItemRef.current) {
        lastItemRef.current.focus();
      }
    }
  }, [volumeDistribution]);

  const handleAddVolumeDistribution = (event: React.SyntheticEvent<HTMLButtonElement>): void => {
    if (onAddVolumeDistribution) {
      onAddVolumeDistribution(event);
    }
    setVolumeDistributionNextFocus(volumeDistribution?.length ? volumeDistribution.length : 0);
  };

  const handleRemoveVolumeDistribution = (index: number, event: React.SyntheticEvent<HTMLButtonElement>): void => {
    if (volumeDistribution?.length && index === volumeDistribution?.length - 1) {
      setVolumeDistributionNextFocus(volumeDistribution?.length - 2);
    } else if (volumeDistribution?.length && index === 0) {
      setVolumeDistributionNextFocus(0);
    } else {
      setVolumeDistributionNextFocus(index);
    }
    if (onRemoveVolumeDistribution) {
      onRemoveVolumeDistribution(index, event);
    }
  };

  return (
    <CampaignCard
      title={t('add_campaign.volume_distribution.title')}
      secondaryLabel={`(${t('add_campaign.volume_distribution.secondary_label')})`}
    >
      <ListContainer>
        <SubLeftHeading>{t('add_campaign.volume_distribution.sub_heading_one')}</SubLeftHeading>
        <SubCenterHeading>{t('add_campaign.volume_distribution.sub_heading_two')}</SubCenterHeading>
        <SubRightHeading />
      </ListContainer>
      <ListItemContainer data-testid="volume-distribution-container">
        {volumeDistribution?.map((volumeDistributionItem, index) => {
          return (
            <VolumeDistributionItem
              ref={index === volumeDistributionNextFocus ? lastItemRef : null}
              key={index}
              volumeDistributionItem={volumeDistributionItem}
              index={index}
              t={t}
              onRemoveVolumeDistribution={(event) => handleRemoveVolumeDistribution(index, event)}
              error={errors ? errors[index] : { distributionDate: undefined, numberOfEnrollees: undefined }}
              onChangeVolumeDistributionItem={
                onChangeVolumeDistributionItem
                  ? (event, key) => onChangeVolumeDistributionItem(index, event, key)
                  : undefined
              }
            />
          );
        })}
      </ListItemContainer>
      <ButtonContainer>
        <StyledButton
          data-testid="add-volume-distribution-button"
          variant={Variants.SECONDARY}
          css={{ padding: '$1 $3' }}
          onClick={(event) => handleAddVolumeDistribution(event)}
        >
          <span>{`${t('add_campaign.volume_distribution.add_button')} +`}</span>
        </StyledButton>
      </ButtonContainer>
    </CampaignCard>
  );
};
